<template>
  <div class="gs-checkout-step">
    <div class="gs-wizard-card gs-checkout-step__card">
      <div class="gs-checkout-step__head">
        <div>
          <h2>Checkout settings</h2>
          <p>Control the checkout experience, required fields, and purchase options.</p>
        </div>
      </div>

      <div class="gs-checkout-step__sections">
        <button
          v-for="tab in checkoutSectionTabs"
          :key="tab.value"
          type="button"
          class="gs-checkout-step__section"
          :class="{ 'is-active': showCheckoutEditorDrawer && checkoutTab === tab.value }"
          @click="handleCheckoutTabChange(tab.value)"
        >
          <div>
            <strong>{{ tab.title }}</strong>
            <span>{{ tab.summary }}</span>
          </div>
          <em>{{ showCheckoutEditorDrawer && checkoutTab === tab.value ? 'Editing' : 'Edit' }}</em>
        </button>
      </div>
    </div>

    <gs-drawer
      v-model="showCheckoutEditorDrawer"
      size="xl"
      :title="activeCheckoutSection.title"
      :subtitle="activeCheckoutSection.summary"
    >
      <section class="gs-checkout-step__drawer">
        <div class="gs-checkout-step__drawer-body">
          <look-feel v-if="checkoutTab === 'lookfeel'" :model="localModel" :customColor="customColor"
            @update-attributes="HandleAttributesUpdate" @updateModel="HandleModelUpdate" :validation="$v" />
          <form-fields v-else-if="checkoutTab === 'formfields'" :model="localModel" @updateModel="HandleModelUpdate"
            :openCustomFieldModal="openCustomFieldModal" />
          <express-checkout v-else-if="checkoutTab === 'expressCheckout'" :model="localModel"
            @updateModel="HandleModelUpdate" />
          <cart-abandonment v-else-if="checkoutTab === 'cartAbandonment'" :model="localModel"
            :openIntegrationModal="openIntegrationModal" :pluginSiteData="pluginSiteData"
            :availableIntegrations="availableIntegrations" :showAbandonCartIntegrationBox="showAbandonCartIntegrationBox"
            :validation="$v" @updateModel="HandleModelUpdate" />

          <language v-else-if="checkoutTab === 'language'" :model="localModel" @updateModel="HandleModelUpdate" />
          <tracking v-else-if="checkoutTab === 'tracking'" :model="localModel" @updateModel="HandleModelUpdate" />
          <legal v-else-if="checkoutTab === 'legal'" :model="localModel" @updateModel="HandleModelUpdate"
            :validation="$v" />
          <quantity v-else-if="checkoutTab === 'quantity'" :model="localModel" @updateModel="HandleModelUpdate"
            :validation="$v" />
          <password v-else-if="checkoutTab === 'password'" :model="localModel" @updateModel="HandleModelUpdate" />
          <advanced-settings v-else-if="checkoutTab === 'advancedSettings'" :model="localModel"
            @updateModel="HandleModelUpdate" />
        </div>
      </section>
    </gs-drawer>

    <gs-modal v-model="showCustomFieldModal" :title="$t('frontend_groovesell_add_new_custom_field')" size="lg">
      <div class="gs-checkout-step__modal-body">
        <gs-field :label="$t('frontend_product_step5_field_name')">
          <text-input v-model="customFieldName" value="customFieldName"
            :placeholder="$t('frontend_product_step5_field_name')" />
        </gs-field>
        <div class="gs-checkout-step__modal-actions">
          <gs-button variant="outline" @click="showCustomFieldModal = false">Cancel</gs-button>
          <gs-button variant="primary" @click="addCustomField">{{ $t('frontend_save') }}</gs-button>
        </div>
      </div>
    </gs-modal>

    <gs-modal v-model="showAbandonCartIntegrationModal" :title="$t('frontend_product_step5_add_new_integration')" size="xl">
      <div class="gs-checkout-step__modal-body">
        <gs-field :label="$t('frontend_product_step5_type')">
          <gd-dropdown :v-bind="integrationType" :value="integrationType" :options="availableIntegrations" size="full"
            @change="setValue($event)" @keyup="setValue($event)" />
        </gs-field>
        <div v-if="integrationType && integrationType != ''" class="gs-checkout-step__plugin-menu">
          <config-menu :key="configMenuKey" :initial-values="componentConfigValues" :plugin-id="pluginId"
            :resources-path="resourcesPath" :server-path="serverPath" :plugin="integrationType"
            :app-data="pluginSiteData" layout="vertical" :is-dark="false" :show-button-borders="false"
            :show-notifications-on-save="false" :opened-from="'groovesell'"
            :btn-label="$t('frontend_groovesell_integration_add_new')" @update="addAbandonCartIntegration"
            @menu-updated="configMenuItemsUpdated"></config-menu>
        </div>
      </div>
    </gs-modal>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';
import Auth from '@groovepages/gd-lib/auth';
import Axios from 'axios';
import ConfigMenu from '@groovepages/gd-ui-kit/components/PluginMenu';
import { required, minLength, url } from 'vuelidate/lib/validators';



import FormFields from './tabs/checkout/FormFields';
import ExpressCheckout from './tabs/checkout/ExpressCheckout';
import CartAbandonment from './tabs/checkout/CartAbandonment';
import Language from './tabs/checkout/Language';
import Tracking from './tabs/checkout/Tracking';
import Legal from './tabs/checkout/Legal';
import Quantity from './tabs/checkout/Quantity';
import Password from './tabs/checkout/Password';
import LookFeel from './tabs/checkout/LookFeel';
import AdvancedSettings from './tabs/checkout/AdvancedSettings';
import { SHOW_REGIONAL_DELIVERY_FEES, forceRegionalDeliveryFeesOff } from './tabs/checkout/shippingSettings';

export default {
  name: 'Step5',
  components: {
    ConfigMenu,
    FormFields,
    ExpressCheckout,
    CartAbandonment,
    Language,
    Tracking,
    Legal,
    Quantity,
    Password,
    LookFeel,
    AdvancedSettings,
  },
  props: {
    model: {
      required: true,
    },
    resourcesPath: {
      required: true,
    },
    serverPath: {
      required: true,
    },
    pluginSiteData: {
      required: true,
    },
    availableIntegrations: {
      required: true,
    },
  },
  data() {
    return {
      inputSize: 'regular',
      headingSize: 'regular',
      full_width: true,
      buttonSize: 'regular',
      lable_color: '#545671',
      heading_color: '#280b14',
      font_family: 'Nunito Sans',
      input_size: '2.5rem',
      button_size: '2rem',
      lable_size: '0.875rem',
      button_text: '14px',
      heading_size: '16px',
      localModel: this.model,
      pricePointsTab: '',
      legalPricePointsTab: '',
      customFieldName: '',
      showCustomFieldModal: false,
      showAbandonCartIntegrationModal: false,
      showCheckoutEditorDrawer: false,
      checkoutTabs: [
        {
          title: this.$t('frontend_product_step5_look_feel'),
          value: 'lookfeel',
          show: true
        },
        {
          title: this.$t('frontend_product_step5_form_fields'),
          value: 'formfields',
          show: true
        },
        {
          title: this.$t('frontend_product_step5_express_checkout'),
          value: 'expressCheckout',
          show: true
        },
        {
          title: this.$t('frontend_product_step5_cart_abondonment'),
          value: 'cartAbandonment',
          show: true
        },
        {
          title: this.$t('frontend_product_step5_custom_css'),
          value: 'tracking',
          show: true
        },
        {
          title: this.$t('frontend_product_step5_legal'),
          value: 'legal',
          show: true
        },
        {
          title: this.$t('frontend_product_step5_language'),
          value: 'language',
          show: true
        },
        /* {
          title: this.$t('frontend_product_step5_compilance'),
          value: 'compliance',
          show: true
        }, */

        {
          title: this.$t('frontend_product_step5_quantity'),
          value: 'quantity',
          show: true
        },
        {
          title: this.$t('frontend_resource_promo_password'),
          value: 'password',
          show: true
        },
        {
          title: this.$t('frontend_product_step5_advanced_settings'),
          value: 'advancedSettings',
          show: true
        },
      ],
      checkoutTab: 'lookfeel',
      billingAgreementTypes: [
        {
          id: 0,
          title: this.$t('frontend_product_step5_billing_types0_title'),
          description: this.$t('frontend_product_step5_billing_types0_desc'),
        },
        {
          id: 1,
          title: this.$t('frontend_product_step5_billing_types1_title'),
          description: this.$t('frontend_product_step5_billing_types1_desc'),
          coming_soon: true,
        },
      ],

      productImage: null,
      integrationType: '',
      configMenuKey: 1,
      componentConfigValues: [],
      pluginId: '',
      integrationType: '',
      pluginMenuItems: [],
      selectedPricePointIndex: 0,
      primary_color: '#d9237f',
      secondary_color: '#ffffff',
    };
  },

  validations: {
    localModel: {
      pricing_points: {
        required,
        minLength: minLength(1),
        $each: {
          minimumQuantity: {
            isValidQuantityType(value, object) {
              if (object.quantity_enable == 1) {
                if (object.minimum_quantity <= object.maximum_quantity) {
                  return true;
                }
                return false;
              }

              return true;
            },
          },
        },
      },
    },
  },

  computed: {
    getCheckoutTabs() {
      return this.checkoutTabs.filter(tab => tab.show)
    },
    checkoutSectionTabs() {
      return this.getCheckoutTabs.map((tab) => ({
        ...tab,
        summary: this.checkoutTabSummary(tab.value),
      }));
    },
    activeCheckoutSection() {
      return this.checkoutSectionTabs.find((tab) => tab.value === this.checkoutTab) || {
        title: this.$t('frontend_product_step5_checkout'),
        summary: this.$t('frontend_product_step5_configure'),
      };
    },
    customColor() {
      const customization = this.localModel.product_customization_details.customization_details;
      const primaryColor = this.localModel.primary_color || this.primary_color || '#d9237f';
      const secondaryColor = this.localModel.secondary_color || this.secondary_color || '#ffffff';

      return {
        '--primary-color': primaryColor,
        '--hover-color': this.hover_color || primaryColor,
        '--disable-color': this.disable_color || '#c7ccdb',
        '--secondary-color': secondaryColor,
        '--font-family': customization.font_family,
        '--lable-color': this.lable_color || customization.lable_color,
        '--heading-color': this.heading_color || customization.heading_color,
        '--input-size': this.input_size,
        '--heading-size': this.heading_size,
        '--button-size': this.button_size,
        '--button-text': this.button_text,
        '--button-padding-top': customization.button_padding_top,
        '--button-padding-bottom': customization.button_padding_bottom,
        '--lable-size': customization.lable_size,
      };
    },

  },

  watch: {
    'localModel.checkout_page_options.look_and_feel.template_type': function (newVal) {
 

      this.localModel.primary_color = this.localModel.primary_color || this.primary_color || '#d9237f';
      this.localModel.secondary_color = this.localModel.secondary_color || this.secondary_color || '#ffffff';
      this.primary_color = this.localModel.primary_color;
      this.secondary_color = this.localModel.secondary_color;
    },

    

    localModel: {
      deep: true,
      handler() {
        this.keepRegionalDeliveryFeesHidden();
        this.$emit('updateModel', this.localModel);
      },
    },
  },

  created() {
    this.keepRegionalDeliveryFeesHidden();
    this.localModel.primary_color = this.localModel.primary_color || this.primary_color || '#d9237f';
    this.localModel.secondary_color = this.localModel.secondary_color || this.secondary_color || '#ffffff';
    this.primary_color = this.localModel.primary_color;
    this.secondary_color = this.localModel.secondary_color;

    this.lable_color =
      this.localModel.product_customization_details.customization_details.lable_color;
    this.heading_color =
      this.localModel.product_customization_details.customization_details.heading_color;
    this.lable_size =
      this.localModel.product_customization_details.customization_details.lable_size;
    this.inputSize =
      this.localModel.product_customization_details.customization_details.input_size;
    this.headingSize =
      this.localModel.product_customization_details.customization_details.heading_size;
    this.button_padding_bottom =
      this.localModel.product_customization_details.customization_details.button_padding_bottom;
    this.button_padding_top =
      this.localModel.product_customization_details.customization_details.button_padding_top;
    this.buttonSize = this.localModel.product_customization_details.customization_details.button_size;

    if (this.inputSize == 'small') {
      this.input_size = '2rem';
    } else if (this.inputSize == 'regular') {
      this.input_size = '2.5rem';
    } else if (this.inputSize == 'large') {
      this.input_size = '3rem';
    } else {
      this.input_size = '2rem';
    }
    if (this.headingSize == 'small') {
      this.heading_size = '14px';
    } else if (this.headingSize == 'regular') {
      this.heading_size = '16px';
    } else if (this.headingSize == 'large') {
      this.heading_size = '20px';
    } else {
      this.heading_size = '16px';
    }

    if (this.buttonSize == 'small') {
      this.button_size = '1rem';
      this.button_text = '12px';
    } else if (this.buttonSize == 'regular') {
      this.button_size = '2rem';
      this.button_text = '12px';
    } else if (this.buttonSize == 'large') {
      this.button_size = '4rem';
      this.button_text = '18px';
    } else {
      this.button_size = '2rem';
      this.button_text = '14px';
    }
  },


  methods: {
    keepRegionalDeliveryFeesHidden() {
      forceRegionalDeliveryFeesOff(
        this.localModel
        && this.localModel.checkout_page_options
        && this.localModel.checkout_page_options.advanced_settings
      );
    },
    HandleModelUpdate(newVal) {
      this.localModel = newVal;
    },

    HandleAttributesUpdate({ key, value }) {
      if (key == 'button_size') this.changedButtonSize(value);
      if (key == 'input_size') this.changedInputSize(value);
      if (key == 'heading_size') this.changedHeadingSize(value);
      if (key == 'primary_color' || key == 'secondary_color') {
        this.localModel[key] = value;
        if (!this.localModel.checkout_page_options.look_and_feel) {
          this.$set(this.localModel.checkout_page_options, 'look_and_feel', {});
        }
        if (this.localModel.checkout_page_options.look_and_feel.template_type !== 4) {
          this.$set(this.localModel.checkout_page_options.look_and_feel, 'template_type', 4);
        }
        if (!this.localModel.checkout_page_options.look_and_feel.custom_color) {
          this.$set(this.localModel.checkout_page_options.look_and_feel, 'custom_color', {});
        }
        this.$set(this.localModel.checkout_page_options.look_and_feel.custom_color, key, value);
      }
      if (key == 'heading_color' || key == 'lable_color') {
        this.localModel.product_customization_details.customization_details[key] = value;
      }
      if (this[key] !== undefined) this[key] = value;
    },
    openIntegrationModal() {
      const e = new CustomEvent('openIntegrations', { details: {} });
      document.dispatchEvent(e)
    },

    changedButtonSize(size) {
      if (size == 'small') {
        this.buttonSize = 'small';
        this.button_size = '1rem';
        this.button_padding_bottom = '1rem';
        this.button_padding_top = '1rem';
        this.button_text = '12px';
        this.localModel.product_customization_details.customization_details.button_padding_bottom = '1rem';
        this.localModel.product_customization_details.customization_details.button_padding_top = '1rem';
      } else if (size == 'regular') {
        this.buttonSize = 'regular';
        this.button_size = '2rem';
        this.button_padding_bottom = '1.5rem';
        this.button_text = '12px';
        this.localModel.product_customization_details.customization_details.button_padding_bottom = '1.5rem';
        this.button_padding_top = '1.5rem';
        this.localModel.product_customization_details.customization_details.button_padding_top = '1.5rem';
      } else if (size == 'large') {
        this.buttonSize = 'large';
        this.button_size = '4rem';
        this.button_text = '18px';
      } else {
        this.buttonSize = 'regular';
        this.button_size = '2rem';
        this.localModel.product_customization_details.customization_details.button_padding_bottom = '1rem';
        this.localModel.product_customization_details.customization_details.button_padding_top = '1rem';
        this.button_text = '14px';
      }
    },
    changedInputSize(size) {
      if (size == 'small') {
        this.inputSize = 'small';
        this.input_size = '2rem';
        this.localModel.product_customization_details.customization_details.lable_size =
          '0.700rem';
        this.lable_size = '0.700rem';
      } else if (size == 'regular') {
        this.inputSize = 'regular';
        this.lable_size = '0.875rem';
        this.localModel.product_customization_details.customization_details.lable_size =
          '0.875rem';
        this.input_size = '2.5rem';
      } else if (size == 'large') {
        this.inputSize = 'large';
        this.input_size = '3rem';
        this.lable_size = '1rem';
        this.localModel.product_customization_details.customization_details.lable_size =
          '1rem';
      } else {
        this.lable_size = '0.875rem';
        this.inputSize = 'regular';
        this.input_size = '2rem';
        this.localModel.product_customization_details.customization_details.lable_size =
          '0.700rem';
      }
    },
    changedHeadingSize(size) {
      if (size == 'small') {
        this.hedingSize = 'small';
        this.heading_size = '14px';
      } else if (size == 'regular') {
        this.hedingSize = 'regular';
        this.heading_size = '16px';
      } else if (size == 'large') {
        this.hedingSize = 'large';
        this.heading_size = '20px';
      } else {
        this.heading_size = '16px';
      }
    },



    showAbandonCartIntegrationBox(index) {
      this.selectedPricePointIndex = index;
      this.integrationType = '';
      this.showAbandonCartIntegrationModal = true;
    },

    setValue(value) {
      this.integrationType = value;
      this.pluginId = '';
      this.componentConfigValues = [];
      this.configMenuKey = this.configMenuKey + 1;
    },

    addAbandonCartIntegration(data) {
      const integrationConfig = data.config;
      const self = this;

      const integrationInfo = [];

      for (const index in integrationConfig) {
        const value = integrationConfig[index];

        if (value) {
          self.pluginMenuItems.forEach((menuValue, menuIndex) => {
            if (menuValue.key == index) {
              const { options } = menuValue;

              const objChecker = options.some((val) => {
                return typeof val === 'object';
              });

              let correspondingOption = value;
              if (objChecker) {
                correspondingOption = options.find((o) => o.value === value);
              }

              const singleIntegrationField = {};
              singleIntegrationField.index = index;
              singleIntegrationField.value = value;
              singleIntegrationField.label = menuValue.label;

              if (objChecker) {
                singleIntegrationField.name = correspondingOption.text;
              } else {
                singleIntegrationField.name = value;
              }

              integrationInfo.push(singleIntegrationField);
            }
          });
        }
      }

      const completeIntegrationInfo = {};
      completeIntegrationInfo.platform = this.integrationType.split('-')[0];
      completeIntegrationInfo.config = integrationInfo;

      if (
        this.localModel.pricing_points[this.selectedPricePointIndex]
          .abandon_cart_integrations
      ) {
        this.localModel.pricing_points[
          this.selectedPricePointIndex
        ].abandon_cart_integrations.push(completeIntegrationInfo);
      } else {
        this.localModel.pricing_points[
          this.selectedPricePointIndex
        ].abandon_cart_integrations = [];
        this.localModel.pricing_points[
          this.selectedPricePointIndex
        ].abandon_cart_integrations.push(completeIntegrationInfo);
      }

      this.showAbandonCartIntegrationModal = false;
    },

    configMenuItemsUpdated(data) {
      this.pluginMenuItems = data.menuItems;
    },
    openCustomFieldModal() {
      this.customFieldName = '';
      this.showCustomFieldModal = true;
    },

    addCustomField() {
      if (!this.customFieldName.length) {
        return false;
      }
      const lastFormFieldOrder =
        this.localModel.checkout_page_options.form_fields[
          this.localModel.checkout_page_options.form_fields.length - 1
        ].order;
      this.localModel.checkout_page_options.form_fields.push({
        order: lastFormFieldOrder + 1,
        type: 'custom',
        label: this.customFieldName,
        enabled: 1,
      });

      this.customFieldName = '';
      this.showCustomFieldModal = false;
    },

    validate() {
      this.$v.$touch();
      const isValid = !this.$v.$invalid;
      this.$emit('on-validate', isValid);
      return isValid;
    },

    handleCheckoutTabChange(newTab) {
      this.checkoutTab = newTab;
      this.showCheckoutEditorDrawer = true;
    },
    checkoutTabSummary(value) {
      const checkoutOptions = this.localModel.checkout_page_options || {};

      if (value === 'lookfeel') {
        return 'Template, colors, typography, and product image';
      }

      if (value === 'formfields') {
        const enabledFields = (checkoutOptions.form_fields || []).filter((field) => field.enabled).length;
        return `${enabledFields} checkout form ${enabledFields === 1 ? 'field' : 'fields'} enabled`;
      }

      if (value === 'expressCheckout') {
        return checkoutOptions.express_checkout ? 'Express checkout enabled' : 'Express checkout disabled';
      }

      if (value === 'cartAbandonment') {
        const integrations = (this.localModel.pricing_points || []).reduce((total, pricePoint) => {
          return total + ((pricePoint.abandon_cart_integrations || []).length);
        }, 0);
        return `${integrations} cart abandonment ${integrations === 1 ? 'integration' : 'integrations'}`;
      }

      if (value === 'tracking') {
        return checkoutOptions.tracking_codes ? 'Custom CSS/tracking configured' : 'No custom CSS/tracking added';
      }

      if (value === 'legal') {
        return 'Terms, policies, consent, and legal display options';
      }

      if (value === 'language') {
        return 'Customize checkout language and labels';
      }

      if (value === 'quantity') {
        const quantityEnabled = (this.localModel.pricing_points || []).some((pricePoint) => pricePoint.quantity_enable == 1);
        return quantityEnabled ? 'Quantity limits configured' : 'Quantity settings disabled';
      }

      if (value === 'password') {
        return 'Optional password collection settings';
      }

      if (value === 'advancedSettings') {
        const advanced = checkoutOptions.advanced_settings || {};
        const allowlistOn = !!(advanced.allowed_countries && advanced.allowed_countries.enabled);
        if (!SHOW_REGIONAL_DELIVERY_FEES) {
          return allowlistOn ? 'Country allowlist enabled' : 'Country allowlist';
        }
        const feesOn = !!(advanced.regional_delivery_fees && advanced.regional_delivery_fees.enabled);
        if (allowlistOn && feesOn) {
          return 'Country allowlist and regional delivery fees enabled';
        }
        if (allowlistOn) {
          return 'Country allowlist enabled';
        }
        if (feesOn) {
          return 'Regional delivery fees enabled';
        }
        return 'Country allowlist and regional delivery fees';
      }

      return 'Configure this checkout setting';
    },

  },
};
</script>

<style>

.gs-checkout-step__head {
  margin-bottom: 18px;
}

.gs-checkout-step__head h2 {
  margin: 0;
  color: var(--gs-foreground);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.gs-checkout-step__head p {
  margin: 6px 0 0;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.45;
}

.gs-checkout-step__sections {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 18px;
}

.gs-checkout-step__section {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;
  min-height: 76px;
  padding: 14px 16px;
  text-align: left;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: var(--gs-shadow-sm);
  cursor: pointer;
  transition: background .18s ease, border-color .18s ease, box-shadow .18s ease;
}

.gs-checkout-step__section:hover,
.gs-checkout-step__section:focus-visible {
  border-color: var(--gs-active-border);
  box-shadow: 0 0 0 3px var(--gs-active-shadow), var(--gs-shadow-sm);
}

.gs-checkout-step__section.is-active {
  background: var(--gs-active-soft);
  border-color: var(--gs-active);
  box-shadow: 0 0 0 3px var(--gs-active-shadow), var(--gs-shadow-sm);
}

.gs-checkout-step__section strong {
  display: block;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
  line-height: 1.35;
}

.gs-checkout-step__section span {
  display: block;
  margin-top: 4px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.4;
}

.gs-checkout-step__section em {
  min-width: 76px;
  padding: 9px 14px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  text-align: center;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
  opacity: 0;
  transition: opacity .16s ease, color .16s ease, border-color .16s ease, background .16s ease;
}

.gs-checkout-step__section:hover em,
.gs-checkout-step__section:focus-visible em,
.gs-checkout-step__section:focus-within em {
  opacity: 1;
}

.gs-checkout-step__section.is-active em {
  color: var(--gs-active);
  background: var(--gs-surface);
  border-color: var(--gs-active-border);
}

.gs-drawer:has(.gs-checkout-step__drawer) {
  width: min(96vw, 1440px);
}

.gs-checkout-step__drawer {
  min-height: 100%;
  background: var(--gs-surface);
}

.gs-checkout-step__drawer-body {
  padding: 0;
}

.gs-checkout-step__modal-body {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 8px 0 0;
}

.gs-checkout-step__modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 6px;
}

.gs-checkout-step__plugin-menu {
  padding-top: 4px;
}

.gs-checkout-rule-editor {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.gs-checkout-default-card,
.gs-checkout-price-rules,
.gs-checkout-price-rule {
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: var(--gs-shadow-sm);
}

.gs-checkout-default-card {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 18px;
}

.gs-checkout-default-card__copy small {
  display: block;
  margin-bottom: 8px;
  color: var(--gs-primary);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.gs-checkout-default-card__copy h4,
.gs-checkout-price-rules__head h4 {
  margin: 0;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
  line-height: 1.35;
}

.gs-checkout-default-card__copy p,
.gs-checkout-price-rules__head p {
  margin: 8px 0 0;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.45;
}

.gs-checkout-default-card__copy {
  max-width: 760px;
}

.gs-checkout-default-card__body,
.gs-checkout-price-rule__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.gs-checkout-choice-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.gs-checkout-choice {
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr);
  column-gap: 12px;
  row-gap: 4px;
  min-height: 104px;
  padding: 16px;
  text-align: left;
  background: #ffffff;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-checkout-choice.is-selected {
  background: var(--gs-active-soft);
  border-color: var(--gs-active);
  box-shadow: 0 0 0 3px var(--gs-active-shadow), var(--gs-shadow-sm);
}

.gs-checkout-choice.is-disabled {
  opacity: .55;
  cursor: not-allowed;
}

.gs-checkout-choice > span {
  width: 18px;
  height: 18px;
  margin-top: 1px;
  border: 2px solid var(--gs-border-strong);
  border-radius: 999px;
}

.gs-checkout-choice.is-selected > span {
  border-color: var(--gs-active);
  box-shadow: inset 0 0 0 4px #ffffff;
  background: var(--gs-active);
}

.gs-checkout-choice strong {
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
  line-height: 1.35;
}

.gs-checkout-choice em {
  grid-column: 2;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  font-style: normal;
  line-height: 1.45;
}

.gs-checkout-inline-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.gs-checkout-inline-fields label,
.gs-checkout-rich-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.gs-checkout-inline-fields span,
.gs-checkout-rich-field label {
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 600;
}

.gs-checkout-price-rules {
  padding: 18px;
}

.gs-checkout-price-rules__head {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--gs-border);
}

.gs-checkout-price-rule {
  margin-top: 12px;
  overflow: hidden;
}

.gs-checkout-price-rule.is-custom {
  border-color: var(--gs-active);
}

.gs-checkout-price-rule.is-disabled {
  opacity: .62;
}

.gs-checkout-price-rule__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
}

.gs-checkout-price-rule__head strong {
  display: block;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
}

.gs-checkout-price-rule__head span {
  display: block;
  margin-top: 4px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.35;
}

.gs-checkout-price-rule__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

.gs-checkout-price-rule__actions em {
  padding: 7px 10px;
  color: var(--gs-muted-foreground);
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  background: var(--gs-surface-subtle);
  border-radius: var(--gs-radius-sm);
}

.gs-checkout-price-rule__actions em.is-custom {
  color: var(--gs-active);
  background: var(--gs-active-soft);
}

.gs-checkout-price-rule__actions label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  font-weight: 600;
}

.gs-checkout-price-rule__actions button,
.gs-checkout-secondary-button {
  min-height: 38px;
  padding: 0 14px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  font-weight: 600;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-checkout-price-rule__actions button:hover,
.gs-checkout-price-rule__actions button:focus-visible,
.gs-checkout-secondary-button:hover,
.gs-checkout-secondary-button:focus-visible {
  color: var(--gs-active);
  border-color: var(--gs-active-border);
  box-shadow: 0 0 0 3px var(--gs-active-shadow);
}

.gs-checkout-price-rule__body {
  padding: 0 16px 16px;
}

.gs-checkout-rule-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.gs-checkout-integration-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.gs-checkout-integration-list article {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-checkout-integration-list strong {
  display: block;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
}

.gs-checkout-integration-list span {
  display: block;
  margin-top: 4px;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.4;
}

.gs-checkout-integration-list button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--gs-primary);
  font-size: 13px;
  font-weight: 600;
  background: transparent;
  border: 0;
}

.gs-checkout-empty-note {
  padding: 14px 16px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  line-height: 1.45;
  background: var(--gs-surface-subtle);
  border: 1px dashed var(--gs-border);
  border-radius: var(--gs-radius-sm);
}

@media (max-width: 980px) {
  .gs-checkout-step__sections {
    grid-template-columns: 1fr;
  }

  .gs-checkout-default-card {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .gs-checkout-choice-row,
  .gs-checkout-inline-fields {
    grid-template-columns: 1fr;
  }

  .gs-checkout-price-rule__head {
    flex-direction: column;
    align-items: stretch;
  }

  .gs-checkout-price-rule__actions {
    justify-content: flex-start;
  }
}

.custom-font-family {
  font-family: var(--font-family) !important;
}

.custom-lable-color {
  color: var(--lable-color) !important;
}

.custom-heading-color {
  color: var(--heading-color) !important;
}

.custom-input-height {
  height: var(--input-size) !important;
}

.preview input::-webkit-input-placeholder {
  font-size: var(--lable-size) !important;
}

.custom-label-size {
  font-size: var(--lable-size) !important;
}

.custom-heading-size {
  font-size: var(--heading-size) !important;
}

.custom-button {
  padding-top: var(--button-padding-top) !important;
  padding-bottom: var(--button-padding-bottom) !important;
  height: var(--button-size) !important;
  font-size: var(--button-text) !important;
}

.disabled-button-custom-template-color-primary {
  background-color: var(--disable-color) !important;
}

.custom-template-color-primary {
  background-color: var(--primary-color) !important;
}

.custom-template-color-primary-border {
  color: var(--primary-color) !important;
}

.custom-template-color-primary-text {
  color: var(--primary-color) !important;
}

.custom-template-color-primary:hover {
  background-color: var(--primary-color) !important;
}

.custom-template-color-secondary {
  background-color: var(--secondary-color) !important;
}

.custom-template-color-secondary-text {
  color: var(--secondary-color) !important;
}

.hover-custom-template-color-primary:hover {
  background-color: var(--hover-color) !important;
}

.custom-template-color-secondary {
  color: var(--secondary-color) !important;
}

.custom-border {
  border-color: var(--primary-color) !important;
}
</style>
